import { needsBoardingConfirm, planDateUpdates, rowKey } from '../model/applyPlan';
import type { VesselChangeJob } from '../types/vesselChanges';
import { updateJobDates } from './vesselChangesMutations';

export interface ApplyVesselChangesInput {
  companyId: number;
  /** The jobs to update: one job ("Use PTP's times") or every job of a call ("Update all N jobs to PTP"). */
  jobs: VesselChangeJob[];
  /** Row keys whose boarding month change the user confirmed. */
  confirmedKeys: ReadonlySet<string>;
}

export interface RefusedJob {
  jobNo: string;
  reason: string;
}

export interface ApplyVesselChangesOutcome {
  updated: VesselChangeJob[];
  refused: RefusedJob[];
  /** Flagged boarding month rows not confirmed: not sent. */
  leftForConfirmation: VesselChangeJob[];
  /** Jobs PTP has no times for: nothing to send. */
  noPtpTimes: VesselChangeJob[];
}

const errorText = (error: unknown): string => (error instanceof Error && error.message ? error.message : 'Request failed');

/** Updates the jobs to PTP's ETA and ETD through the existing bulk update. */
export const applyVesselChanges = async ({
  companyId, jobs, confirmedKeys,
}: ApplyVesselChangesInput): Promise<ApplyVesselChangesOutcome> => {
  const leftForConfirmation = jobs.filter((job) => needsBoardingConfirm(job) && !confirmedKeys.has(rowKey(job)));
  const toSend = jobs.filter((job) => !leftForConfirmation.includes(job));
  const noPtpTimes = toSend.filter((job) => !job.ptpEta && !job.ptpEtd);
  const outcome: ApplyVesselChangesOutcome = { updated: [], refused: [], leftForConfirmation, noPtpTimes };

  for (const plan of planDateUpdates(companyId, toSend)) {
    try {
      const answer = await updateJobDates(plan.request);
      plan.jobs.forEach((job) => {
        const result = answer?.results?.find((row) => Number(row.saleOrderId) === job.saleOrderId);
        if (result?.ok) outcome.updated.push(job);
        else outcome.refused.push({ jobNo: job.jobNo, reason: result?.message || 'Not updated' });
      });
    } catch (error) {
      plan.jobs.forEach((job) => outcome.refused.push({ jobNo: job.jobNo, reason: errorText(error) }));
    }
  }

  return outcome;
};
